import React, { useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import tw from "twin.macro";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import { useForm } from "../Form/context";
import { useInputGroup } from "../InputGroup/context";
import { HiCheck, HiChevronDown, HiX } from "react-icons/hi";
import Spinner from "../Spinner";
import { SIZES } from "../utils/constant";
import { CONTROL_SIZES } from "../utils/constant";

const downImg = "/assets/icons-svg/chevron-down-gray.svg";
const cancelImg = "/assets/icons-svg/x-close.svg";

// Multi-select option with checkbox
const MultiSelectOption = ({
  innerProps,
  label,
  selectProps,
  isSelected,
  isDisabled,
  isFocused,
}) => {
  const { themeColor } = selectProps;
  
  return (
    <div
      className={`select-option ${isSelected && "selected"} ${
        isDisabled && "disabled"
      }`}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
            justifyContent: "flex-start",
        padding: '8px 12px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        backgroundColor: 'white',
      }}
    >
      {/* Custom Checkbox */}
      <div
        style={{
          width: '18px',
          height: '18px',
          border: isSelected ? '2px solid #f59e0b' : '2px solid #d1d5db',
          borderRadius: '4px',
          marginRight: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          flexShrink: 0,
        }}
      >
        {isSelected && (
          <HiCheck style={{ color: '#f59e0b', fontSize: '14px', fontWeight: 'bold' }} />
        )}
      </div>
      <span style={{ fontSize: '14px', color: '#374151' }}>{label}</span>
    </div>
  );
};

// Single select option (original style)
const DefaultOption = ({
  innerProps,
  label,
  selectProps,
  isSelected,
  isDisabled,
}) => {
  const { themeColor } = selectProps;
  return (
    <div
      className={`select-option ${isSelected && "selected"} ${
        isDisabled && "disabled"
      }`}
      {...innerProps}
    >
      <span className="ml-2">{label}</span>
      {isSelected && (
        <HiCheck className={`text-${themeColor} darks:text-white text-xl`} />
      )}
    </div>
  );
};

const DefaultDropdownIndicator = () => {
  return (
    <div className="select-dropdown-indicator mr-2">
      <img src={downImg} />
    </div>
  );
};

const DefaultClearIndicator = (props) => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <div className="select-clear-indicator">
        <img src={cancelImg} />
      </div>
    </div>
  );
};

const DefaultLoadingIndicator = ({ selectProps }) => {
  const { themeColor } = selectProps;
  return <Spinner className={`select-loading-indicatior text-${themeColor}`} />;
};

const Select = React.forwardRef((props, ref) => {
  const {
    size,
    style,
    className,
    form,
    field,
    components,
    componentAs,
    readOnly,
    isMulti,
    ...rest
  } = props;
  const disabled = readOnly ? true : props.isDisabled;
  const [searchValue, setSearchValue] = useState();

  const themeColor = "";
  const controlSize = "";
  const primaryColorLevel = "";
  const mode = "";
  const formControlSize = "useForm()?.size";
  const inputGroupSize = useInputGroup()?.size;

  const selectSize = size || inputGroupSize || formControlSize || controlSize;

  const ComponentToRender = componentAs || ReactSelect;

  const twColor = { red: { "500": "#ef4444" } };
  const twHeight = {
    7: "1.75rem",
    9: "2.25rem",
    11: "2.75rem",
    14: "3.5rem",
  };

  let isInvalid = false;

  if (!isEmpty(form)) {
    const { touched, errors } = form;
    const touchedField = get(touched, field.name);
    const errorField = get(errors, field.name);
    isInvalid = touchedField && errorField;
  }

  const getBoxShadow = (state) => {
    const shadaowBase = "0 0 0 1px ";
    if (isInvalid) {
      return shadaowBase + twColor.red["500"];
    }
    if (state.isFocused) {
      return;
    }
    return "none";
  };

  const styles = {
    control: (provided, state) => {
      return {
        ...provided,
        height: twHeight[CONTROL_SIZES[selectSize]],
        minHeight: twHeight[CONTROL_SIZES[selectSize]],
        "&:hover": {
          boxShadow: getBoxShadow(state),
          cursor: "pointer",
        },
        boxShadow: getBoxShadow(state),
        borderRadius: '0.375rem',
        ...(isInvalid ? { borderColor: twColor.red["500"] } : {}),
      };
    },
    input: (css) => {
      return {
        ...css,
        input: {
          outline: "none",
          outlineOffset: 0,
          boxShadow: "none !important",
        },
      };
    },
    option: (provided, state) => ({
      ...provided,
      backgroundColor: isMulti ? 'white !important' : (state.isFocused ? '#f3f4f6' : 'white'),
      color: '#374151',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: isMulti ? 'white !important' : '#f3f4f6',
      },
      '&:active': {
        backgroundColor: isMulti ? 'white !important' : '#f3f4f6',
      },
      ':active': {
        backgroundColor: isMulti ? 'white !important' : '#f3f4f6',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: 'absolute',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    ...style,
  };

  const selectClass = classNames("select", `select-${selectSize}`, className);

  // Choose the appropriate Option component based on isMulti
  const OptionComponent = isMulti ? MultiSelectOption : DefaultOption;

  return (
    <ComponentToRender
      className={selectClass}
      classNamePrefix={"select"}
      ref={ref}
      styles={styles}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
        },
      })}
      themeColor={`${themeColor}-${primaryColorLevel}`}
      components={{
        IndicatorSeparator: () => null,
        Option: OptionComponent,
        LoadingIndicator: DefaultLoadingIndicator,
        DropdownIndicator: DefaultDropdownIndicator,
        ClearIndicator: DefaultClearIndicator,
        ...components,
      }}
      options={props.options}
      value={props.value}
      onChange={props.onChange}
      isDisabled={disabled}
      isMulti={isMulti}
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={document.body}
      menuShouldBlockScroll={false}
      maxMenuHeight={200}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      {...field}
      {...rest}
      onInputChange={props.handleSearchChange}
    />
  );
});

Select.propTypes = {
  size: PropTypes.oneOf([SIZES.LG, SIZES.MD, SIZES.SM]),
  componentAs: PropTypes.elementType,
  readOnly: PropTypes.bool,
  isMulti: PropTypes.bool,
};

Select.defaultProps = {
  componentAs: ReactSelect,
  isMulti: false,
};

export default Select;